import React, { useState } from "react";
import {
  ComposedChart,
  Bar,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { FaArrowUp, FaArrowDown } from "react-icons/fa";
import StatCard from "../ui/StatCard";
import ChartCard from "../ui/CardChart";

const VampAnalytics = ({ data, loading }) => {
  // Hover state — must come before any early returns
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [activeIndex, setActiveIndex] = useState(null);

  if (!data) return null;

  const chartData = Array.isArray(data.chartData) ? data.chartData : [];
  if (chartData.length === 0) return null;

  // Default KPI month = latest entry in chart data
  const latestMonthData = chartData[chartData.length - 1];

  // Decide which month drives KPIs (hovered month or latest)
  const activeMonth =
    activeIndex != null && chartData[activeIndex] ? chartData[activeIndex] : latestMonthData;

  // Use backend-provided refundRate if present, otherwise show "—"
  const activeRefundRate =
    activeMonth && Number.isFinite(activeMonth.refundRate) ? activeMonth.refundRate : null;

  // Handle refunds being null (pipeline lag) vs real 0
  const refundsValue =
    activeMonth?.refunds == null ? data.refund_count : activeMonth.refunds;

  const refundsSubtitle =
    activeRefundRate == null ? "Refund Rate: —" : `Refund Rate: ${activeRefundRate.toFixed(2)}%`;

  return (
    <>
      {/* ===== KPI CARDS ===== */}
      <div className="flex flex-row flex-nowrap gap-4 mb-4 w-full">
        <div className="flex-1 min-h-36">
          <StatCard
            icon={FaArrowUp}
            title="Total Transactions"
            value={activeMonth?.transactions ?? data.denom_txn_count}
            sublabel={activeMonth?.month}
            color="from-purple-500 to-purple-600"
          />
        </div>

        <div className="flex-1 min-h-28">
          <StatCard
            icon={FaArrowDown}
            title="Total Refunds"
            value={refundsValue ?? data.refund_count}
            subtitle={refundsSubtitle}
            color="from-pink-500 to-pink-600"
          />
        </div>
      </div>

      {/* ===== MONTHLY CHART ===== */}
      <ChartCard title="💳 VAMP Performance (Monthly)" loading={loading}>
        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart
            data={chartData}
            onMouseMove={(state) => {
              if (state?.activeTooltipIndex != null) setActiveIndex(state.activeTooltipIndex);
            }}
            onMouseLeave={() => setActiveIndex(null)}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />

            {/* Left axis: Transactions */}
            <YAxis
              yAxisId="left"
              orientation="left"
              tickFormatter={(v) => Number(v).toLocaleString()}
              allowDecimals={false}
            />

            {/* Right axis: Refund Rate (%) */}
            <YAxis
              yAxisId="right"
              orientation="right"
              tickFormatter={(v) => `${Number(v).toFixed(2)}%`}
              allowDecimals
              domain={[0, "dataMax + 0.25"]}
            />

            <Tooltip
              formatter={(value, name) => {
                if (name === "Refund Rate (%)") return [`${Number(value).toFixed(2)}%`, name];
                return [Number(value).toLocaleString(), name];
              }}
              labelFormatter={(label) => `Month: ${label}`}
            />

            <Legend />

            {/* Bars: Volume (always looks good) */}
            <Bar
              yAxisId="left"
              dataKey="transactions"
              name="Transactions"
              radius={[6, 6, 0, 0]}
            />

            {/* Line: Refund Rate (always visible, even when refunds are 5) */}
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="refundRate"
              name="Refund Rate (%)"
              dot={{ r: 3 }}
              strokeWidth={2}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </ChartCard>
    </>
  );
};

export default VampAnalytics;
